import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import * as depositService from '@/supabase/deposit/service/depositService';
import { DepositIncomingCard } from '@/supabase/deposit/types/deposit';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { isPaidPlanId } from '@/lib/planAccess';
import { isStaleDepositError, isSettledOrderDepositError } from '@/supabase/deposit/depositErrors';
import { logError } from '@/lib/sentry';

/**
 * 정산서 좌측 '입금 들어옴' 파랑 카드 + 실시간(realtime) push.
 * - service는 날짜 무관 전체(확실·금액정확·미확정)를 1회 로드(perf: 날짜변경 시 재fetch 안 함).
 * - selectedDate 필터는 client-side(useMemo) — 그리드(useOrdersByDate)와 동일하게 format(d,'yyyy-MM-dd').
 * - 실시간: deposit_match 변경 구독 → (디바운스) 재조회. 새로 들어온(현재 날짜) 카드만 flash/토스트.
 * - confirm: 진행 중엔 전 카드 잠금 + 성공 후 큐 재조회(stale 카드 정리). confirm 중 realtime refresh는 스킵.
 * - 참고 후보(회색 카드)의 /order 노출은 오너 결정(D8, 2026-07-06)으로 제거 — /deposits 전용.
 */
export function useDepositIncoming(selectedDate: Date, refreshOrders: () => Promise<void>) {
  const { user, isAuthenticating } = useAuth();
  const { subscription } = useSubscription();
  // 무료 플랜(및 구독 미확정)은 비로그인 경로를 탄다 — 카드·행 칩·도착 토스트·fetch/realtime 전부 off.
  const userId = isPaidPlanId(subscription?.subscription_plan_id) ? (user?.id ?? null) : null;
  const [allCards, setAllCards] = useState<DepositIncomingCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  // 진행 중 액션 종류 — 스피너를 누른 버튼(✓ 또는 ✗)에만 표시하기 위함(codexloop #5).
  const [pendingAction, setPendingAction] = useState<'confirm' | 'reject' | null>(null);
  // 실시간으로 방금 도착한 카드 matchId — "방금" 배지 + flash 애니메이션용(2.5초 후 해제).
  const [justArrivedIds, setJustArrivedIds] = useState<Set<number>>(() => new Set());
  // 방금 확정된 카드 — 토스트 대신 칩 자리에 '확정됨 · 되돌리기'를 5초간 인라인 표시(자동 소멸).
  const [justConfirmedCards, setJustConfirmedCards] = useState<DepositIncomingCard[]>([]);

  // stale-request 가드: 마지막 요청만 state에 반영.
  const reqIdRef = useRef(0);
  // 확정 재진입 가드(state closure보다 즉시 반영) — 동시 RPC + realtime refresh 경합 차단.
  const confirmingRef = useRef(false);
  // confirm 진행 중 도착한 realtime 이벤트를 드롭하지 않고 보류 → confirm 종료 후 1회 드레인.
  const pendingRealtimeRef = useRef(false);
  // 이미 화면에 보였던 matchId 집합 — 첫 로드는 flash 안 함, 이후 새로 들어온 것만 flash.
  const seenIdsRef = useRef<Set<number>>(new Set());
  const hasLoadedRef = useRef(false);
  // flash 해제 타이머 — 발화 후 제거 + 언마운트/셀러전환 시 일괄 정리(누적 방지).
  const flashTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  // 인라인 '확정됨' 자동 소멸 타이머(matchId별) — 5초 후 제거 + 언마운트/셀러전환 시 정리.
  const confirmedTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  // refresh가 selectedDate에 의존하지 않게(=재구독/재fetch churn 방지) 최신 날짜를 ref로.
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);

  const refresh = useCallback(async () => {
    if (!userId) return;
    const reqId = ++reqIdRef.current;
    setLoading(true); setError(null);
    try {
      const next = await depositService.loadDepositIncoming(userId);
      if (reqId !== reqIdRef.current) return; // stale 응답 폐기
      // 첫 로드 이후, 현재 보고 있는 날짜에 새로 들어온 카드만 flash("방금") + 토스트.
      if (hasLoadedRef.current) {
        const today = format(selectedDateRef.current, 'yyyy-MM-dd');
        const arrived = next
          .filter((c) => c.orderDate === today && !seenIdsRef.current.has(c.matchId))
          .map((c) => c.matchId);
        if (arrived.length > 0) {
          setJustArrivedIds((prev) => {
            const n = new Set(prev);
            arrived.forEach((id) => n.add(id));
            return n;
          });
          const timer = setTimeout(() => {
            flashTimersRef.current.delete(timer);
            setJustArrivedIds((prev) => {
              const n = new Set(prev);
              arrived.forEach((id) => n.delete(id));
              return n;
            });
          }, 2500);
          flashTimersRef.current.add(timer);
          toast.info(`입금 ${arrived.length}건이 들어왔습니다`);
        }
      }
      seenIdsRef.current = new Set(next.map((c) => c.matchId));
      hasLoadedRef.current = true;
      setAllCards(next);
    } catch (err) {
      logError('useDepositIncoming.refresh', err);
      if (reqId === reqIdRef.current) {
        // fail-closed: 카드를 비워 stale 카드로 잘못 확정하는 일을 막는다.
        setAllCards([]);
        setError(err as Error);
      }
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [userId]);

  // 셀러 전환 시 realtime baseline 리셋 — 새 셀러의 첫 로드를 '도착'으로 오인해 전부 flash하는 것 방지.
  // (refresh 트리거 effect보다 먼저 선언 → userId 변경 시 리셋이 먼저 적용됨.)
  useEffect(() => {
    seenIdsRef.current = new Set();
    hasLoadedRef.current = false;
    flashTimersRef.current.forEach(clearTimeout);
    flashTimersRef.current.clear();
    setJustArrivedIds(new Set());
    // 셀러 전환 시 인라인 확정 카드/타이머도 정리(이전 셀러 카드가 새 셀러 화면에 남지 않게).
    confirmedTimersRef.current.forEach(clearTimeout);
    confirmedTimersRef.current.clear();
    setJustConfirmedCards([]);
  }, [userId]);

  useEffect(() => {
    if (!userId && !isAuthenticating) {
      // 비로그인 확정 → 카드 비우고 로딩 종료.
      reqIdRef.current++;
      setAllCards([]);
      setLoading(false);
      return;
    }
    if (userId) refresh();
  }, [userId, isAuthenticating, refresh]);

  // 실시간: deposit_match 변경(새 입금 INSERT, 확정 UPDATE) → (디바운스) 큐 재조회.
  // - confirm 진행 중엔 스킵(confirm 자체 refresh가 처리, self-confirm UPDATE 경합 방지).
  // - 디바운스 400ms: burst insert/연쇄 UPDATE에 refresh 폭주 방지.
  useEffect(() => {
    if (!userId) return;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const onChange = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        // ★발화 시점에 재확인: confirm 중이면 드롭하지 말고 보류(confirm finally가 드레인).
        if (confirmingRef.current) { pendingRealtimeRef.current = true; return; }
        refresh();
      }, 400);
    };
    const unsubscribe = depositService.subscribeToDepositMatches(userId, onChange, (status, err) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        logError('useDepositIncoming.realtime', err ?? new Error(status));
      }
    });
    return () => {
      if (debounce) clearTimeout(debounce);
      unsubscribe();
    };
  }, [userId, refresh]);

  // 언마운트 후 늦게 도착하는 응답이 state를 건드리지 않게(stale-write 방지) + flash 타이머 정리.
  useEffect(() => () => {
    reqIdRef.current++;
    flashTimersRef.current.forEach(clearTimeout);
    flashTimersRef.current.clear();
    confirmedTimersRef.current.forEach(clearTimeout);
    confirmedTimersRef.current.clear();
  }, []);

  // 인라인 '확정됨' 타이머 정리 헬퍼.
  const clearConfirmedTimer = useCallback((matchId: number) => {
    const t = confirmedTimersRef.current.get(matchId);
    if (t) { clearTimeout(t); confirmedTimersRef.current.delete(matchId); }
  }, []);

  // 확정 카드를 5초 인라인 표시 큐에 추가 + 자동 소멸 타이머. 같은 matchId 중복은 갱신.
  const addJustConfirmed = useCallback((card: DepositIncomingCard) => {
    setJustConfirmedCards((prev) => [...prev.filter((c) => c.matchId !== card.matchId), card]);
    clearConfirmedTimer(card.matchId);
    const timer = setTimeout(() => {
      confirmedTimersRef.current.delete(card.matchId);
      setJustConfirmedCards((prev) => prev.filter((c) => c.matchId !== card.matchId));
    }, 5000);
    confirmedTimersRef.current.set(card.matchId, timer);
  }, [clearConfirmedTimer]);

  // selectedDate 기준 client-side 필터(그리드가 로드한 그 날 주문과 정렬). 재fetch 없음.
  // 미확정 카드 + 방금 확정된 카드(5초 인라인)를 합류 — 확정 카드는 confirmedMatchIds로 구분 렌더.
  const cards = useMemo(() => {
    const target = format(selectedDate, 'yyyy-MM-dd');
    const pending = allCards.filter((c) => c.orderDate === target);
    const confirmed = justConfirmedCards.filter(
      (c) => c.orderDate === target && !pending.some((p) => p.matchId === c.matchId),
    );
    return [...pending, ...confirmed];
  }, [allCards, justConfirmedCards, selectedDate]);

  // 방금 확정된 matchId 집합 — 칩/카드가 '확정됨 · 되돌리기' 모드로 렌더할지 판정.
  // 크로스탭에서 다른 기기가 그 매칭을 되돌리면 같은 입금이 pending(allCards)으로 재유입된다.
  // 그때는 confirmed에서 제외 → '확정됨' 오표시 + 서버가 거부할 undo 노출을 막는다(cards 합류의 dedup과 동일 규칙).
  const confirmedMatchIds = useMemo(() => {
    const target = format(selectedDate, 'yyyy-MM-dd');
    const pendingIds = new Set(allCards.filter((c) => c.orderDate === target).map((c) => c.matchId));
    return new Set(justConfirmedCards.filter((c) => !pendingIds.has(c.matchId)).map((c) => c.matchId));
  }, [justConfirmedCards, allCards, selectedDate]);

  // 확정 직후 5초 '되돌리기'. undo_deposit_match RPC가 상태(입금완료→미지정)+다리학습(refcount)까지
  // 되돌린다(출고완료면 서버가 거부). 토스트 액션에서만 호출 — 별도 노출 안 함.
  const undoConfirm = useCallback(async (card: DepositIncomingCard) => {
    // 인라인 확정칩 즉시 제거(낙관적 + 되돌리기 버튼 연타 차단) + 자동소멸 타이머 정리.
    clearConfirmedTimer(card.matchId);
    setJustConfirmedCards((prev) => prev.filter((c) => c.matchId !== card.matchId));
    try {
      // 지문 운반 계약(스펙 §3.3): 카드에 로드 시 부착된 지문만 전달(액션 시점 재계산 금지).
      await depositService.undoMatch(card.matchId, card.paidThreshold, card.totalPrice, card.shippingFeeOverride, card.settingsFingerprint);
    } catch (err) {
      logError('useDepositIncoming.undo', err);
      // 출고완료 거부·응답 분실·네트워크 단절 모두에서 정확한 문구(failure 단언 대신 '결과 불명') + 확인 경로 제시.
      toast.error('되돌리기 결과를 확인하지 못했습니다. 입금매칭에서 상태를 확인해주세요');
      // 낙관적 복원 대신 서버 진실로 동기화 — 응답 분실(서버는 undo 성공)·영구 거부(출고완료)·일시 실패를
      // 모두 정확히 반영한다: 미확정으로 풀렸으면 일반 칩 복귀, 확정이 유지됐으면 비표시(영구실패 무한 재시도/false 복원 차단).
      const failResults = await Promise.allSettled([refresh(), refreshOrders()]);
      failResults.forEach((r) => {
        if (r.status === 'rejected') logError('useDepositIncoming.undo.fail.refresh', r.reason);
      });
      return;
    }
    toast.success('확정을 되돌렸습니다');
    // 입금 큐 재조회(카드 복귀) + 정산서 갱신(주문 노랑→미지정).
    const results = await Promise.allSettled([refresh(), refreshOrders()]);
    results.forEach((r) => {
      if (r.status === 'rejected') logError('useDepositIncoming.undo.refresh', r.reason);
    });
  }, [refresh, refreshOrders, clearConfirmedTimer]);

  const confirm = useCallback(async (card: DepositIncomingCard) => {
    // 재진입 가드: 한 번에 하나의 확정만(동시 RPC/refreshOrders 경합 방지).
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirmingId(card.matchId);
    setPendingAction('confirm');
    try {
      let confirmed = false;
      try {
        // 단계3: linkMatch = confirm + 다리학습(멱등). amount_suggested(이름미지정) 확정도 학습 → 다음 자동매칭.
        // card.version(modified_at) 전달 → 낙관적 동시성: 다른 탭/기기가 그 사이 거부/변경했으면 RPC가 거부.
        await depositService.linkMatch(
          card.matchId, card.orderId, card.version, card.paidThreshold,
          // 지문 운반 계약(스펙 §3.3): 카드에 로드 시 부착된 지문만 전달(액션 시점 재계산 금지).
          card.totalPrice, card.shippingFeeOverride, 'order', card.settingsFingerprint,
        );
        confirmed = true;
      } catch (err) {
        logError('useDepositIncoming.confirm', err);
        // stale(P0003, 구 RPC 40001 호환) = 카드 로드 후 매칭/주문 금융이 바뀜. 카드 정리 + 안내.
        if (isStaleDepositError(err)) {
          toast.error('이 입금은 처리 상태가 바뀌었습니다. 목록을 새로고침했습니다');
          void refresh(); // stale 카드 제거(다른 탭의 거부/변경 반영).
        } else if (isSettledOrderDepositError(err)) {
          // P0002 = 카드 로드 후 주문이 완납/출고됨(예: 부분 입금 주문을 출고 처리) — 재시도 무의미.
          toast.info('이미 입금·출고 처리가 끝난 주문이에요. 목록을 새로고침했습니다');
          void refresh(); // 완납/출고 주문 카드는 후보 필터에서 빠져 사라진다.
        } else {
          toast.error('확정에 실패했습니다. 다시 시도해주세요');
        }
      }
      if (!confirmed) return; // RPC 실패 → 카드 유지(finally가 락 해제).

      // 확정 성공: 즉시 해당 카드 제거(피드백 + 같은 카드 재클릭 차단). 나머지는 아래 refresh가 정리.
      setAllCards((prev) => prev.filter((c) => c.matchId !== card.matchId));
      // refresh/refreshOrders 실패가 '확정 실패'로 위장하지 않게 분리(이미 확정 성공).
      // 토스트 대신 칩이 있던 자리에 '확정됨 · 되돌리기'를 5초간 인라인 표시(자동 소멸).
      // 되돌리기는 인라인 버튼이 undoConfirm을 호출(상태+다리학습 원복).
      addJustConfirmed(card);
      // 입금 큐 재조회(다른 stale 카드 정리) + 정산서 그리드 갱신(주문 → 입금완료 노랑).
      // allSettled: 한쪽이 먼저 reject해도 둘 다 끝날 때까지 대기 → 락이 refresh 도중 풀리지 않음.
      const results = await Promise.allSettled([refresh(), refreshOrders()]);
      results.forEach((r) => {
        if (r.status === 'rejected') logError('useDepositIncoming.confirm.refresh', r.reason);
      });
    } finally {
      // ★락을 post-confirm refresh까지 유지 — refresh가 stale 카드를 무효화하기 전에
      //   다른 카드 확정/실시간 refresh가 끼어드는 경합을 막는다.
      confirmingRef.current = false;
      setConfirmingId(null);
      setPendingAction(null);
      // confirm 중 보류된 realtime 이벤트가 있으면 1회 드레인(누락 입금 반영).
      if (pendingRealtimeRef.current) {
        pendingRealtimeRef.current = false;
        refresh();
      }
    }
  }, [refresh, refreshOrders, addJustConfirmed]);

  // ✗(제외): 이 입금이 이 주문 게 아니면 주문 매칭에서 제외(rejectMatch→seller_rejected) → /deposits '매칭 제외'로.
  // 주문 상태는 무변경(행은 미지정 그대로, 칩만 사라짐). confirm과 같은 전역 락 공유(한 번에 하나의 액션).
  const reject = useCallback(async (card: DepositIncomingCard) => {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirmingId(card.matchId);
    setPendingAction('reject');
    try {
      let rejected = false;
      try {
        await depositService.rejectMatch(card.matchId);
        rejected = true;
      } catch (err) {
        logError('useDepositIncoming.reject', err);
        toast.error('제외 처리에 실패했습니다. 다시 시도해주세요');
      }
      if (!rejected) return;
      setAllCards((prev) => prev.filter((c) => c.matchId !== card.matchId));
      toast.success('주문 매칭에서 제외했습니다', {
        description: "입금매칭 > '매칭 제외'에서 다시 복구할 수 있습니다",
      });
      // 입금 큐만 재조회(칩 제거). 주문 상태 안 바뀌니 refreshOrders 불필요.
      const results = await Promise.allSettled([refresh()]);
      results.forEach((r) => {
        if (r.status === 'rejected') logError('useDepositIncoming.reject.refresh', r.reason);
      });
    } finally {
      confirmingRef.current = false;
      setConfirmingId(null);
      setPendingAction(null);
      if (pendingRealtimeRef.current) {
        pendingRealtimeRef.current = false;
        refresh();
      }
    }
  }, [refresh]);

  // 진행 중이면 전 카드 잠금(특정 카드만이 아니라 전체) — confirmingId 단일값 경합 방지.
  const confirming = confirmingId !== null;

  return { cards, loading, error, confirm, reject, confirming, confirmingId, pendingAction, refresh, justArrivedIds, confirmedMatchIds, undoConfirm };
}
